import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ArrowLeft, Upload, X, FileText, Download, Trash2 } from 'lucide-react';

const EditarGasto = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [nuevosArchivos, setNuevosArchivos] = useState([]);
  const [formData, setFormData] = useState({
    monto: '',
    concepto: '',
    descripcion: '',
    categoria_id: '',
    fecha_gasto: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch gasto
      const gastoRes = await fetch(`${API_URL}/api/gastos/${id}`, { headers });
      if (gastoRes.ok) {
        const data = await gastoRes.json();
        setFormData({
          monto: data.monto,
          concepto: data.concepto,
          descripcion: data.descripcion || '',
          categoria_id: data.categoria_id,
          fecha_gasto: data.fecha_gasto
        });
      }

      // Fetch categorias
      const categoriasRes = await fetch(`${API_URL}/api/gastos/categorias`, { headers });
      if (categoriasRes.ok) {
        const data = await categoriasRes.json();
        setCategorias(data);
      }

      // Fetch archivos
      const archivosRes = await fetch(`${API_URL}/api/gastos/${id}/archivos`, { headers });
      if (archivosRes.ok) {
        const data = await archivosRes.json();
        setArchivos(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNuevosArchivos([...nuevosArchivos, ...files]);
  };

  const removeNewFile = (index) => {
    setNuevosArchivos(nuevosArchivos.filter((_, i) => i !== index));
  };

  const handleDeleteArchivo = async (archivoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este archivo?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/${id}/archivos/${archivoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setArchivos(archivos.filter(a => a.id !== archivoId));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar el archivo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      // Update gasto
      const response = await fetch(`${API_URL}/api/gastos/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al actualizar el gasto');
      }

      // Upload new files
      if (nuevosArchivos.length > 0) {
        for (const archivo of nuevosArchivos) {
          const formDataFile = new FormData();
          formDataFile.append('archivo', archivo);

          await fetch(`${API_URL}/api/gastos/${id}/archivos`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formDataFile
          });
        }
      }

      alert('Gasto actualizado exitosamente');
      navigate(`/egresos/${id}`);
    } catch (error) {
      console.error('Error:', error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate(`/egresos/${id}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Volver al gasto
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Editar Gasto</h1>
          <p className="text-gray-600 mt-1">Modifica la información del gasto</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
          {/* Información básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monto *</label>
              <input
                type="number"
                step="0.01"
                value={formData.monto}
                onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoría *</label>
              <select
                value={formData.categoria_id}
                onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Selecciona una categoría</option>
                {categorias.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Concepto *</label>
            <input
              type="text"
              value={formData.concepto}
              onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha del Gasto *</label>
            <input
              type="date"
              value={formData.fecha_gasto}
              onChange={(e) => setFormData({ ...formData, fecha_gasto: e.target.value })}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          {/* Archivos existentes */}
          {archivos.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Archivos Actuales</label>
              <div className="space-y-2">
                {archivos.map((archivo) => (
                  <div key={archivo.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-700">{archivo.nombre_archivo}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteArchivo(archivo.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nuevos archivos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Agregar Nuevos Archivos</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input
                type="file"
                onChange={handleFileChange}
                multiple
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="h-10 w-10 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">Click para seleccionar archivos</p>
              </label>
            </div>

            {nuevosArchivos.length > 0 && (
              <div className="mt-4 space-y-2">
                {nuevosArchivos.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm text-gray-700">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeNewFile(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate(`/egresos/${id}`)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EditarGasto;